import type {
  RequestPriority as DbRequestPriority,
  RequestStatus as DbRequestStatus,
} from '@cortex/database';
import {
  Badge,
  Button,
  Card,
  Center,
  Heading,
  Inline,
  List,
  ListItem,
  Stack,
  Text,
} from '@cortex/ui';
import { AlertTriangle, ArrowRight } from 'lucide-react';

import {
  mapDbPriorityToUiPriority,
  mapDbStatusToUiStatus,
} from '@/lib/request-mappers';

interface AttentionRequest {
  id: string;
  title: string;
  status: DbRequestStatus;
  priority: DbRequestPriority | null;
  request_number: number;
}

const STATUS_BADGE_VARIANT: Record<
  string,
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'success'
  | 'warning'
  | 'info'
  | 'outline'
> = {
  draft: 'secondary',
  pending: 'warning',
  'in-progress': 'info',
  review: 'default',
  completed: 'success',
  cancelled: 'destructive',
};

const PRIORITY_BADGE_VARIANT: Record<
  string,
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'success'
  | 'warning'
  | 'info'
  | 'outline'
> = {
  low: 'secondary',
  medium: 'outline',
  high: 'warning',
  urgent: 'destructive',
};

interface NeedsAttentionListProps {
  requests: AttentionRequest[];
  onRequestClick: (requestNumber: number) => void;
}

export function NeedsAttentionList({
  requests,
  onRequestClick,
}: NeedsAttentionListProps) {
  return (
    <Card>
      <Inline gap="sm" className="border-border border-b p-4">
        <AlertTriangle className="text-warning h-5 w-5" />
        <Heading level={2} className="font-medium">
          Needs Attention
        </Heading>
        {requests.length > 0 && (
          <Badge variant="warning" className="ml-1">
            {requests.length}
          </Badge>
        )}
      </Inline>

      {requests.length === 0 ? (
        <Center className="text-foreground/60 py-10">
          <Text size="sm">Nothing needs attention right now</Text>
        </Center>
      ) : (
        <List divided>
          {requests.map((request) => {
            const uiStatus = mapDbStatusToUiStatus(request.status);
            const uiPriority = request.priority
              ? mapDbPriorityToUiPriority(request.priority)
              : null;

            return (
              <ListItem key={request.id}>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onRequestClick(request.request_number)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left"
                >
                  <Stack gap="none" className="min-w-0 flex-1">
                    <Inline gap="sm">
                      <Text
                        as="span"
                        color="muted"
                        size="xs"
                        className="font-mono"
                      >
                        #{request.request_number}
                      </Text>
                      <Text
                        as="span"
                        weight="medium"
                        truncate
                        className="text-sm"
                      >
                        {request.title}
                      </Text>
                    </Inline>
                  </Stack>
                  <Inline gap="sm" className="shrink-0">
                    <Badge
                      variant={STATUS_BADGE_VARIANT[uiStatus] ?? 'secondary'}
                    >
                      {uiStatus}
                    </Badge>
                    {uiPriority && (
                      <Badge
                        variant={
                          PRIORITY_BADGE_VARIANT[uiPriority] ?? 'outline'
                        }
                      >
                        {uiPriority}
                      </Badge>
                    )}
                    <ArrowRight className="text-foreground/40 h-4 w-4" />
                  </Inline>
                </Button>
              </ListItem>
            );
          })}
        </List>
      )}
    </Card>
  );
}
